/**
 * /*
 * Copyright 2017-2017 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License"). You may not use this file except in compliance with
 * the License. A copy of the License is located at
 *
 *     http://aws.amazon.com/apache2.0/
 *
 * or in the "license" file accompanying this file. This file is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
 * CONDITIONS OF ANY KIND, either express or implied. See the License for the specific language governing permissions
 * and limitations under the License.
 *
 * @format
 */

import * as React from "react";

import { Storage } from "aws-amplify";

import { onError } from "app/AppSupport/Utils";

interface IProps {
    src?: object | string;
    imgKey?: string;
    path?: string;
    body?: string;
    contentType?: string;
    level: string;
    track?: string;
    identityId: string;
    onLoad?: any;
    onError?: any;
    fileToKey?: any;
    theme: any;
    style: any;
    selected?: any;
    onClick?: any;
}

interface IState {
    src: object | string;
}

export default class S3Image extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.handleClick = this.handleClick.bind(this);
        const initSrc = this.props.src || "";
        this.state = { src: initSrc };
    }

    public getImageSource = (key, level, track, identityId) => {
        if (!Storage || typeof Storage.get !== "function") {
            const err = new Error("No Amplify Storage module found");
            onError(null, err);
        }
        Storage.get(key, { level: level ? level : "public", track, identityId })
            .then((url) => {
                this.setState({
                    src: url,
                });
            })
            .catch((err) => {
                onError(null, err);
            });
    };

    public load() {
        const { imgKey, path, body, contentType, level, track, identityId } =
            this.props;
        if (!imgKey && !path) {
            const err = new Error("empty imgKey and path");
            onError(null, err);
            return;
        }

        const that = this;
        const key = imgKey || path || "";
        if (body) {
            const type = contentType || "binary/octet-stream";
            if (!Storage || typeof Storage.put !== "function") {
                const err = new Error("No Storage module found");
                onError(null, err);
            }
            const ret = Storage.put(key, body, {
                contentType: type,
                // @ts-ignore
                level: level ? level : "public",
                // @ts-ignore
                track,
            });
            ret.then((data) => {
                that.getImageSource(key, level, track, identityId);
            }).catch((err) => {
                onError(null, err);
            });
        } else {
            that.getImageSource(key, level, track, identityId);
        }
    }

    public handleOnLoad = (evt) => {
        const { onLoad } = this.props;
        if (onLoad) {
            onLoad(this.state.src);
        }
    };

    public handleOnError = (imgKey: string, evt) => {
        try {
            throw new Error(
                `Error in S3ImageCP module with image file: ${imgKey}`
            );
        } catch (err) {
            onError(null, err, { imgKey });
        }
    };

    public handleClick(evt) {
        const { onClick } = this.props;
        if (onClick) {
            onClick(evt);
        }
    }

    public componentDidMount() {
        this.load();
    }

    public componentDidUpdate(prevProps) {
        const update =
            prevProps.path !== this.props.path ||
            prevProps.imgKey !== this.props.imgKey ||
            prevProps.body !== this.props.body;
        if (update) {
            this.load();
        }
    }

    public imageEl = (src, theme, imgKey) => {
        if (!src) {
            return null;
        }

        const { selected } = this.props;
        return (
            <div style={{ position: "relative" }} onClick={this.handleClick}>
                <img
                    style={theme.photoImg}
                    src={src}
                    onLoad={this.handleOnLoad}
                    onError={this.handleOnError.bind(null, imgKey)}
                    alt={"Pallene visual"}
                />
                <div
                    style={selected ? theme.overlaySelected : theme.overlay}
                ></div>
            </div>
        );
    };

    public render() {
        const { style, imgKey } = this.props;
        const src = this.state.src;
        if (!src) {
            return null;
        }

        const theme = this.props.theme;
        const photoStyle = { ...theme.photo, ...style };

        return (
            <div style={photoStyle}>
                {photoStyle ? this.imageEl(src, theme, imgKey) : null}
            </div>
        );
    }
}
